import React, { useContext, useEffect, useState } from "react"
import { VibratoClientContext } from "./contexts"
import { Building, Plus, Check, HelpCircle, NavArrowRight, Clock, Edit } from "iconoir-react"
import { useNavigate } from "react-router-dom"
import { Link } from "react-router-dom"
import { BusinessVerificationRequest } from "@/lib/vibrato-api"

interface VerifiedBusiness {
  uuid: string
  name: string
  city_name: string | null
  state: string | null
  phone: string | null
  website_url: string | null
  short_description: string | null
}

const VerifiedBusinessList: React.FC = () => {
  const vibratoClient = useContext(VibratoClientContext)
  const [verificationRequests, setVerificationRequests] = useState<BusinessVerificationRequest[]>([])
  const [verifiedBusinesses, setVerifiedBusinesses] = useState<VerifiedBusiness[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      if (vibratoClient) {
        try {
          setIsLoading(true)
          const [requestsResponse, businessesResponse] = await Promise.all([
            vibratoClient.listBusinessVerificationRequests({ pageSize: 50 }),
            vibratoClient.getVerifiedBusinesses()
          ])
          setVerificationRequests(requestsResponse.results)
          setVerifiedBusinesses(businessesResponse)
        } catch (error) {
          console.error("Failed to fetch data:", error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchData()
  }, [vibratoClient])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Approved
          </span>
        )
      case "pending":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Pending
          </span>
        )
      case "rejected":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            Rejected
          </span>
        )
      case "cancelled":
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            Cancelled
          </span>
        )
      default:
        return null
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <Check className="w-5 h-5 text-green-500" />
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-500" />
      case "rejected":
        return <HelpCircle className="w-5 h-5 text-red-500" />
      case "cancelled":
        return <HelpCircle className="w-5 h-5 text-gray-500" />
      default:
        return <HelpCircle className="w-5 h-5 text-gray-500" />
    }
  }

  // Filter out approved requests since they'll show as verified businesses
  const pendingRequests = verificationRequests.filter((req) => req.status !== "approved")
  const hasAnyData = verifiedBusinesses.length > 0 || pendingRequests.length > 0

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-2xl shadow-md">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4" />
            <p className="text-gray-600">Loading businesses...</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col bg-white rounded-2xl shadow-md">
          <div className="w-full">
            {/* Header with Add Button */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Your Businesses</h2>
                  <p className="text-gray-600 text-sm">Manage your verified businesses and verification requests</p>
                </div>
                <button
                  onClick={() => navigate("/business/verification/new")}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Verify Business
                </button>
              </div>
            </div>

            {hasAnyData ? (
              <>
                {/* Table for md and larger screens */}
                <div className="hidden md:block overflow-auto rounded-lg">
                  <table className="w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Business Name
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Location
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Verified Businesses */}
                      {verifiedBusinesses.map((business) => (
                        <tr key={`business-${business.uuid}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Check className="w-5 h-5 text-green-500" />
                              <div className="ml-3">
                                <div className="text-base font-medium text-gray-900">{business.name}</div>
                                <div className="text-sm text-gray-500">Verified Business</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {[business.city_name, business.state].filter(Boolean).join(", ")}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Verified
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">Business</div>
                          </td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <div className="flex gap-2 justify-end">
                              <Link to={`/business/edit/${business.uuid}`}>
                                <button className="p-2 text-purple-600 hover:text-purple-900 bg-purple-50 rounded-lg transition-colors">
                                  <Edit className="w-5 h-5" />
                                </button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {/* Pending/Rejected/Cancelled Verification Requests */}
                      {pendingRequests.map((request) => (
                        <tr key={`request-${request.uuid}`} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(request.status)}
                              <div className="ml-3">
                                <div className="text-base font-medium text-gray-900">{request.business.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {[request.business.city, request.business.state].filter(Boolean).join(", ")}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(request.status)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">Request</div>
                          </td>
                          <td className="px-6 py-4 text-right whitespace-nowrap">
                            <div className="flex gap-2 justify-end">
                              <Link to={`/business/verification/${request.uuid}`}>
                                <button className="p-2 text-indigo-600 hover:text-indigo-900 bg-indigo-50 rounded-lg transition-colors">
                                  <NavArrowRight className="w-5 h-5" />
                                </button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Card layout for mobile devices */}
                <div className="md:hidden rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-t border-gray-200">
                    <div className="flex text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="w-full">Your Businesses</div>
                    </div>
                  </div>

                  {/* Verified Businesses */}
                  {verifiedBusinesses.map((business) => (
                    <div
                      key={`business-${business.uuid}`}
                      className="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex flex-col">
                          <div className="flex items-center mb-2">
                            <Check className="w-5 h-5 text-green-500" />
                            <span className="text-lg font-medium text-gray-900 ml-2">{business.name}</span>
                          </div>
                          <div className="text-sm text-gray-500 mb-1">Verified Business</div>
                          <div className="text-sm text-gray-500">
                            {[business.city_name, business.state].filter(Boolean).join(", ")}
                          </div>
                        </div>
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Verified
                        </span>
                      </div>

                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/business/edit/${business.uuid}`}>
                          <button className="p-2 text-purple-600 hover:text-purple-900 bg-purple-50 rounded-lg transition-colors">
                            <Edit className="w-5 h-5" />
                          </button>
                        </Link>
                      </div>
                    </div>
                  ))}

                  {/* Pending Verification Requests */}
                  {pendingRequests.map((request) => (
                    <div
                      key={`request-${request.uuid}`}
                      className="p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex flex-col">
                          <div className="flex items-center mb-2">
                            {getStatusIcon(request.status)}
                            <span className="text-lg font-medium text-gray-900 ml-2">{request.business.name}</span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {[request.business.city, request.business.state].filter(Boolean).join(", ")}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {getStatusBadge(request.status)}
                      </div>

                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/business/verification/${request.uuid}`}>
                          <button className="p-2 text-indigo-600 hover:text-indigo-900 bg-indigo-50 rounded-lg transition-colors">
                            <NavArrowRight className="w-5 h-5" />
                          </button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-fit p-12">
                <div className="flex flex-col items-center justify-center">
                  <div className="p-4 bg-purple-100 rounded-full mb-4">
                    <Building className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No businesses yet</h3>
                  <p className="text-gray-600 text-center max-w-md">
                    Get started by verifying your first business to unlock business features and tools.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default VerifiedBusinessList
